/**
 * AVBP Consent & Privacy content — the VERBATIM owner-approved copy that the
 * mandatory Stage 0 of every intake renders before a client can proceed.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  SOURCE OF TRUTH. The text below is transcribed verbatim from the
 *  owner-approved spine_content draft:
 *    Owner's Inbox/04_Projects/AVBP/spine_content/
 *      AVBP_SPINE_CONTENT_Consent_and_Concerns_v1_2026-06-15.html  (Part 1)
 *  Owner ruling (this dispatch): "wire the drafted consent text AS-IS as the
 *  mandatory always-on FIRST stage." Do NOT reword. If the owner revises the
 *  copy, bump CONSENT_VERSION and update both this file and the source HTML.
 *
 *  The §7 co-brand passage is Partner-gated (OQ-3): it is shown ONLY when the
 *  engagement is via a channel partner. It is held here as a separate field so
 *  the renderer can include/omit it without editing the body.
 *
 *  The five tick-boxes map to: 4 REQUIRED consent flags + 1 OPTIONAL gate for
 *  astrology/health data (D7 + D6). `required:true` boxes ALL must be ticked
 *  before the stage can advance (acceptanceComplete()).
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * The consent copy version. Stored on every captured acceptance so a returning
 * client's prior acceptance can be re-prompted if the copy materially changes.
 * Tied to the spine_content draft version (v1, 2026-06-15).
 */
export const CONSENT_VERSION = 'consent/v3-2026-06-24' as const;

/** A consent section = a heading + one or more paragraphs and/or a bullet list. */
export interface ConsentSection {
  heading: string;
  /** Plain paragraphs (HTML is NOT interpreted — rendered as text). */
  paras?: string[];
  /** Optional bullet list rendered under the paragraphs. */
  bullets?: string[];
  /** Set when this section is shown only in a partner engagement (§7, OQ-3). */
  partnerOnly?: boolean;
}

/** A single tick-box the client must confirm. */
export interface ConsentCheckItem {
  /** Stable key persisted into the acceptance record. */
  key: string;
  /** The verbatim label shown beside the box. */
  label: string;
  /** Required boxes gate advancement; the optional box does not. */
  required: boolean;
}

/** The intro shown above the sections. */
export const CONSENT_INTRO = {
  heading: 'Your information, and how we look after it',
  para:
    'Thank you for trusting Aayatan Veda with your space and your concerns. ' +
    'Before we begin, here is exactly what we collect, where it is kept, and ' +
    'the rights you keep over it. Please read this and tick each box — it ' +
    'takes a minute, and it means we both start with clarity.',
} as const;

/** The body sections (§1–§7), verbatim from the spine_content draft. */
export const CONSENT_SECTIONS: ConsentSection[] = [
  {
    heading: '1. What we collect',
    paras: ['Depending on the service you have chosen, we may collect:'],
    bullets: [
      'Who you are — your name, phone, email, and (where relevant) your firm or business details.',
      'Your property — its address and map location, floor plans, drawings, photographs and video of the inside and surroundings, the north direction, and the position of fixtures.',
      'The people connected to the space — names and relationships of family members or business associates, and where each person lives, sits, or works.',
      'Birth details — date, time, and place of birth for any person for whom you ask us to include astrological guidance. This is optional and only used where you opt in.',
      'Health information — only where you have chosen a wellness/Ayurveda-linked service: your stated concerns, past diagnoses you choose to share, and lab reports you upload. You decide what to share.',
      'Your concerns and goals — what you are hoping to improve, in your own words and via the checklist.',
      'Media you upload — photos, videos, voice notes, and documents you send us through the portal or messages.',
    ],
  },
  {
    heading: '2. Where it is stored',
    paras: [
      'Your information is stored securely on Google Firebase, in data centres located in India. Access is limited to the Aayatan Veda team members working on your engagement. We do not sell your data, and we do not share it with advertisers.',
    ],
  },
  {
    heading: '3. How long we keep it',
    paras: [
      'We keep your engagement record — including your intake, reports, and the remedies we recommended — for as long as you remain our client and for ten (10) years after our engagement with you ends, so that we can support follow-ups, re-visits, and any future work on the same property. We call this your ledger: a continuous record we can recall when you return to us, rather than asking you to start from scratch each time. After this retention period ends, records are deleted or anonymised.',
    ],
  },
  {
    heading: '4. Your rights — access, correction, deletion',
    bullets: [
      'You may ask to see the information we hold about you at any time.',
      'You may ask us to correct anything that is wrong.',
      'You may ask us to delete your data. Where we are required to keep certain records for legal or accounting reasons, we will tell you what must be retained and for how long.',
      'You may withdraw your consent for optional items (such as astrology or health data) without affecting the core service.',
    ],
    paras: [
      'To exercise any of these, simply write to us at the contact address in your engagement.',
    ],
  },
  {
    heading: '5. What this report is — and is not',
    paras: [
      'Aayatan Veda offers guidance grounded in Vastu, and where you opt in, in Ayurvedic and astrological tradition. Please understand clearly:',
    ],
    bullets: [
      'Our reports and recommendations are not a substitute for professional medical advice, diagnosis, or treatment. Always consult a qualified doctor for any health condition.',
      'They are not legal advice and should not replace a lawyer for any legal matter.',
      'They are not financial or investment advice.',
      'They are not a structural-engineering or building-safety certification. Any construction, demolition, or structural change must be checked and approved by a licensed architect or structural engineer before you act on it.',
    ],
  },
  {
    heading: '6. Recording of calls and consultations',
    paras: [
      'Our review calls and consultations may be recorded so that our team can prepare your report accurately and so you have a record to refer back to. Recordings are stored with the rest of your engagement data and are not shared outside the team working on your project.',
    ],
  },
  {
    heading: '7. If your work is through a partner',
    partnerOnly: true,
    paras: [
      'Your engagement is being delivered in association with our channel partner. The report you receive may carry their branding alongside ours. Your information is handled by Aayatan Veda under this same note.',
    ],
  },
];

/** The "Please confirm" tick-boxes, verbatim. 4 required + 1 optional. */
export const CONSENT_CHECKS: ConsentCheckItem[] = [
  {
    key: 'info_use',
    label:
      'I have read and understood how my information is collected, stored, and used.',
    required: true,
  },
  {
    key: 'not_a_substitute',
    label:
      'I understand this report is guidance and not a substitute for medical, legal, financial, or structural-engineering advice.',
    required: true,
  },
  {
    key: 'recording',
    label:
      'I consent to my review calls being recorded for the purpose of preparing my report.',
    required: true,
  },
  {
    key: 'ledger',
    label:
      'I consent to Aayatan Veda keeping my engagement record (ledger) so my history can be recalled for follow-ups and future work.',
    required: true,
  },
  {
    // Key retained for stored-acceptance compatibility. As of consent/v3
    // (owner ruling 2026-06-24) this box is REQUIRED: sharing birth/health
    // data stays optional ("where I choose to provide them"), but consent to
    // its use is mandatory — you cannot share without consenting.
    key: 'astro_health_optional',
    label:
      'I consent to my birth details / health information being used for the astrology / wellness portions of my service, where I choose to provide them.',
    required: true,
  },
];

/** The keys of the REQUIRED checks (the advancement gate). */
export const REQUIRED_CONSENT_KEYS: string[] = CONSENT_CHECKS.filter(
  (c) => c.required,
).map((c) => c.key);

export const CONSENT_CONFIRM_HEADING = 'Please confirm' as const;
